import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, Loader2, Tent, X } from "lucide-react";
import { toast } from "sonner";
import AuthShell from "@/components/auth/AuthShell";
import { useAuth } from "@/lib/auth";

// Landing page for a staff-invite link (`/staff/invites/:code`).
// Two actions: accept (POST /campsites/staff/invites/{code}/accept →
// auto-promotes user to owner role) or decline
// (POST /campsites/staff/invites/{code}/decline).
export default function StaffInvite() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { api, user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // "accepted" | "declined"
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!user) {
      const next = encodeURIComponent(`/staff/invites/${code}`);
      navigate(`/login?next=${next}`, { replace: true });
    }
  }, [user, code, navigate]);

  const accept = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/campsites/staff/invites/${code}/accept`);
      setResult(data);
      setDone("accepted");
      await refresh();
      toast.success(`Welcome to the ${data.campsite_name} team!`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not accept invite.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/campsites/staff/invites/${code}/decline`);
      setDone("declined");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not decline invite.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;

  if (done === "accepted") {
    return (
      <AuthShell title="You're in!" subtitle={result?.campsite_name && `You're now staff at ${result.campsite_name}.`}>
        <div className="flex flex-col items-center gap-4 py-2" data-testid="staff-accepted">
          <CheckCircle2 className="w-14 h-14" style={{ color: "var(--brand)" }} />
          <button onClick={() => navigate("/owner")} className="btn btn-green w-full justify-center" data-testid="staff-goto-owner">
            <Tent className="w-4 h-4" /> Open owner dashboard
          </button>
        </div>
      </AuthShell>
    );
  }

  if (done === "declined") {
    return (
      <AuthShell title="Invite declined" subtitle="No hard feelings — we've let the campsite owner know.">
        <div className="flex justify-center py-2">
          <button onClick={() => navigate("/passport")} className="btn btn-light" data-testid="staff-back-passport">
            Back to my passport
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Staff invitation"
      subtitle="A campsite owner has invited you to help manage their campsite."
    >
      <div className="flex flex-col gap-3" data-testid="staff-invite-page">
        <button onClick={accept} disabled={busy} className="btn btn-green justify-center w-full" data-testid="staff-accept">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4" /> Accept &amp; join the team</>}
        </button>
        <button onClick={decline} disabled={busy} className="btn btn-light justify-center w-full"
                style={{ color: "#B45309" }} data-testid="staff-decline">
          <X className="w-4 h-4" /> Decline
        </button>
      </div>
    </AuthShell>
  );
}
